import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, TrendingUp, MousePointerClick, Eye, Search, Brain, Loader2, Info, Plug, RefreshCw, Megaphone, Sparkles, ArrowRight, X } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const COLORS = ["#0055FF", "#3377FF", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

export default function AnalyticsPage() {
  const [range, setRange] = useState(28);
  const [google, setGoogle] = useState(null);
  const [llm, setLlm] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [g, l] = await Promise.all([
        api.get(`/analytics/google/overview?days=${range}`),
        api.get(`/analytics/llm/overview?days=${range}`),
      ]);
      setGoogle(g.data);
      setLlm(l.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [range]);

  return (
    <DashboardLayout
      eyebrow="Insights"
      title="Analytics"
      subtitle="Google Search + GA4 + LLM Visibility. One place to see everything that matters."
      action={
        <div className="flex items-center gap-2">
          <div className="flex bg-zinc-950 border border-zinc-800 rounded-sm p-0.5" data-testid="analytics-range">
            {[7, 14, 28, 90].map((d) => (
              <button
                key={d}
                onClick={() => setRange(d)}
                className={`px-3 py-1.5 rounded-sm text-xs font-medium tracking-wider transition-colors ${range === d ? "bg-[#0055FF] text-white" : "text-zinc-400 hover:text-zinc-200"}`}
                data-testid={`analytics-range-${d}`}
              >
                {d}D
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={refresh} disabled={loading} className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="analytics-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      }
    >
      {loading && !google && !llm ? (
        <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…</div>
      ) : (
        <Tabs defaultValue="google" className="space-y-6" data-testid="analytics-tabs">
          <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm">
            <TabsTrigger value="google" data-testid="analytics-tab-google" className="data-[state=active]:bg-[#0055FF] data-[state=active]:text-white rounded-sm">
              <BarChart3 className="w-4 h-4 mr-2" /> Google
            </TabsTrigger>
            <TabsTrigger value="llm" data-testid="analytics-tab-llm" className="data-[state=active]:bg-[#0055FF] data-[state=active]:text-white rounded-sm">
              <Brain className="w-4 h-4 mr-2" /> LLM Visibility
            </TabsTrigger>
          </TabsList>

          <TabsContent value="google" className="space-y-6">
            {google && <GoogleSection data={google} />}
          </TabsContent>

          <TabsContent value="llm" className="space-y-6">
            {llm && <LlmSection data={llm} />}
          </TabsContent>
        </Tabs>
      )}
    </DashboardLayout>
  );
}

// -------- Google section --------
function GoogleSection({ data }) {
  const { totals, series, top_queries, top_pages, sources, connected, is_demo } = data;
  const [adsTarget, setAdsTarget] = useState(null);
  return (
    <div className="space-y-6">
      <RunAdsDialog target={adsTarget} onClose={() => setAdsTarget(null)} />
      {/* Connection status banner */}
      {(is_demo || !connected.gsc || !connected.ga4) && (
        <div className="border border-[#F59E0B]/30 bg-[#F59E0B]/5 rounded-sm p-4 flex items-start gap-3" data-testid="analytics-demo-banner">
          <Info className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-zinc-300 leading-relaxed flex-1">
            <strong className="text-[#F59E0B]">Demo data</strong> — connect Google Search Console + GA4 to see your real numbers.
            <div className="mt-1 text-zinc-500 text-xs flex gap-3 flex-wrap">
              <span>GSC: <span className={connected.gsc ? "text-[#10B981]" : "text-zinc-400"}>{connected.gsc ? "connected" : "not connected"}</span></span>
              <span>GA4: <span className={connected.ga4 ? "text-[#10B981]" : "text-zinc-400"}>{connected.ga4 ? "connected" : "not connected"}</span></span>
            </div>
          </div>
          <Button asChild size="sm" className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm flex-shrink-0">
            <Link to="/integrations" data-testid="analytics-connect-cta"><Plug className="w-3.5 h-3.5 mr-1.5" /> Connect</Link>
          </Button>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="google-kpis">
        <Kpi label="Sessions" value={totals.sessions.toLocaleString()} icon={TrendingUp} />
        <Kpi label="Clicks" value={totals.clicks.toLocaleString()} icon={MousePointerClick} />
        <Kpi label="Impressions" value={totals.impressions.toLocaleString()} icon={Eye} />
        <Kpi label="Avg CTR" value={`${totals.avg_ctr_pct}%`} icon={MousePointerClick} />
        <Kpi label="Avg position" value={totals.avg_position} icon={Search} />
      </div>

      {/* Chart */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="google-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-medium">Sessions vs Clicks</h3>
          <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px]">{series.length} days</Badge>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={series}>
            <defs>
              <linearGradient id="sessionsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0055FF" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#0055FF" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="clicksGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis dataKey="date" stroke="#52525B" fontSize={11} tickFormatter={(v) => v.slice(5)} />
            <YAxis stroke="#52525B" fontSize={11} />
            <Tooltip contentStyle={{ background: "#0B0B0E", border: "1px solid #27272A", borderRadius: 2, fontSize: 12 }} labelStyle={{ color: "#A1A1AA" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="sessions" stroke="#0055FF" fill="url(#sessionsGrad)" strokeWidth={2} />
            <Area type="monotone" dataKey="clicks" stroke="#10B981" fill="url(#clicksGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Top queries + pages */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="google-top-queries">
          <h3 className="font-display font-medium mb-4">Top queries (Search Console)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                  <th className="pb-2 font-normal">Query</th>
                  <th className="pb-2 font-normal text-right">Clicks</th>
                  <th className="pb-2 font-normal text-right">CTR</th>
                  <th className="pb-2 font-normal text-right">Pos</th>
                  <th className="pb-2 font-normal text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {top_queries.map((q) => {
                  // Underperforming = position 5-20 AND CTR < 5% — these get the highlighted CTA
                  const isOpportunity = q.position >= 5 && q.position <= 20 && (q.ctr || 0) < 5;
                  return (
                    <tr key={q.query} className="border-b border-zinc-900 last:border-0 group">
                      <td className="py-2.5 truncate max-w-[200px]">{q.query}</td>
                      <td className="py-2.5 text-right text-zinc-300">{q.clicks}</td>
                      <td className="py-2.5 text-right text-zinc-400">{q.ctr}%</td>
                      <td className="py-2.5 text-right font-mono text-zinc-400">{q.position}</td>
                      <td className="py-2.5 text-right">
                        <button
                          onClick={() => setAdsTarget(q)}
                          className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest rounded-sm px-2 py-1 transition-colors border ${
                            isOpportunity
                              ? "text-[#10B981] hover:text-white bg-[#10B981]/10 hover:bg-[#10B981] border-[#10B981]/30 hover:border-[#10B981]"
                              : "text-zinc-500 hover:text-zinc-200 bg-zinc-900/50 hover:bg-zinc-800 border-zinc-800 hover:border-zinc-700 opacity-60 group-hover:opacity-100"
                          }`}
                          data-testid={`run-ads-${q.query.replace(/\s+/g, '-').slice(0, 20)}`}
                          title={isOpportunity
                            ? `Opportunity: position ${q.position}, ${q.ctr}% CTR — run paid ads while we boost organic`
                            : `Build a Google Ads campaign for "${q.query}"`}
                        >
                          <Megaphone className="w-3 h-3" /> Ads
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="google-top-pages">
          <h3 className="font-display font-medium mb-4">Top pages (GA4)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                  <th className="pb-2 font-normal">Path</th>
                  <th className="pb-2 font-normal text-right">Sessions</th>
                  <th className="pb-2 font-normal text-right">Bounce</th>
                </tr>
              </thead>
              <tbody>
                {top_pages.map((p) => (
                  <tr key={p.path} className="border-b border-zinc-900 last:border-0">
                    <td className="py-2.5 truncate max-w-[200px] font-mono text-xs text-zinc-300">{p.path}</td>
                    <td className="py-2.5 text-right text-zinc-300">{p.sessions}</td>
                    <td className="py-2.5 text-right text-zinc-400">{p.bounce_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Sources pie */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="google-sources">
        <h3 className="font-display font-medium mb-4">Traffic sources</h3>
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={sources} dataKey="sessions" nameKey="source" innerRadius={50} outerRadius={90} paddingAngle={2}>
              {sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#0B0B0E", border: "1px solid #27272A", borderRadius: 2, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// -------- LLM section --------
function LlmSection({ data }) {
  const { totals, series, top_queries, model_breakdown, is_demo } = data;
  return (
    <div className="space-y-6">
      {is_demo && (
        <div className="border border-[#F59E0B]/30 bg-[#F59E0B]/5 rounded-sm p-4 flex items-start gap-3" data-testid="llm-empty-banner">
          <Info className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-zinc-300 leading-relaxed flex-1">
            <strong className="text-[#F59E0B]">No LLM checks yet</strong> — run brand-mention checks on the LLM Visibility page to populate this dashboard.
          </div>
          <Button asChild size="sm" className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm flex-shrink-0">
            <Link to="/llm-visibility" data-testid="llm-go-cta"><Brain className="w-3.5 h-3.5 mr-1.5" /> Open tracker</Link>
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="llm-kpis">
        <Kpi label="Tracked queries" value={totals.tracked_queries} icon={Search} />
        <Kpi label="Checks (range)" value={totals.checks_in_range} icon={RefreshCw} />
        <Kpi label="Mentions" value={totals.mentions_in_range} icon={TrendingUp} />
        <Kpi label="Mention rate" value={`${totals.mention_rate_pct}%`} icon={Eye} />
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="llm-trend">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-medium">Mention rate over time</h3>
          <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px]">{series.length} days</Badge>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis dataKey="date" stroke="#52525B" fontSize={11} tickFormatter={(v) => v.slice(5)} />
            <YAxis stroke="#52525B" fontSize={11} unit="%" />
            <Tooltip contentStyle={{ background: "#0B0B0E", border: "1px solid #27272A", borderRadius: 2, fontSize: 12 }} />
            <Line type="monotone" dataKey="mention_rate_pct" stroke="#0055FF" strokeWidth={2} dot={{ r: 3, fill: "#0055FF" }} activeDot={{ r: 5 }} name="Mention rate" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="llm-top-queries">
          <h3 className="font-display font-medium mb-4">Top performing queries</h3>
          {top_queries.length === 0 ? (
            <p className="text-sm text-zinc-500">Run some LLM visibility checks to see this.</p>
          ) : (
            <ul className="space-y-2.5">
              {top_queries.map((q) => (
                <li key={q.query} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{q.query}</div>
                    <div className="text-[11px] text-zinc-600">{q.mentions} / {q.checks} checks</div>
                  </div>
                  <div className="w-24 h-1.5 bg-zinc-900 rounded-full overflow-hidden flex-shrink-0">
                    <div className="h-full bg-[#0055FF]" style={{ width: `${Math.min(q.mention_rate_pct, 100)}%` }} />
                  </div>
                  <span className="text-xs text-zinc-300 font-mono w-12 text-right">{q.mention_rate_pct}%</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="llm-models">
          <h3 className="font-display font-medium mb-4">By model</h3>
          {model_breakdown.length === 0 ? (
            <p className="text-sm text-zinc-500">No model data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={model_breakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis type="number" stroke="#52525B" fontSize={11} />
                <YAxis dataKey="model" type="category" stroke="#52525B" fontSize={11} width={80} />
                <Tooltip contentStyle={{ background: "#0B0B0E", border: "1px solid #27272A", borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="checks" fill="#3377FF" name="Checks" />
                <Bar dataKey="mentions" fill="#10B981" name="Mentions" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="label-eyebrow">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 text-zinc-600" />}
      </div>
      <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}


// ============== GSC query → Google Ads campaign dialog ==============
function RunAdsDialog({ target, onClose }) {
  const navigate = useNavigate();
  const [budget, setBudget] = useState(25);
  const [landingUrl, setLandingUrl] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (target) {
      setBudget(25);
      setLandingUrl("");
      setCreated(null);
    }
  }, [target]);

  const create = async () => {
    if (!target) return;
    setCreating(true);
    try {
      const { data } = await api.post("/google-ads/from-gsc-query", {
        query: target.query,
        landing_url: landingUrl || undefined,
        daily_budget_usd: Number(budget),
        impressions: target.impressions,
        position: target.position,
        ctr: target.ctr,
      });
      setCreated(data);
      toast.success("Campaign built and saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  if (!target) return null;
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm sm:max-w-lg" data-testid="run-ads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#10B981]" />
            Run Google Ads on this query
          </DialogTitle>
          <DialogDescription className="text-zinc-400 leading-relaxed">
            Capture revenue <strong className="text-white">now</strong> while we work on organic ranking. We'll build a full Google Ads campaign (3 RSAs + keyword groups + negatives + CSV).
          </DialogDescription>
        </DialogHeader>

        {!created ? (
          <div className="space-y-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 space-y-1.5">
              <div className="text-xs text-zinc-500">Target query</div>
              <div className="font-mono text-sm text-zinc-100">"{target.query}"</div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div><div className="text-zinc-500">Position</div><div className="font-mono text-zinc-200">{target.position?.toFixed(1)}</div></div>
                <div><div className="text-zinc-500">CTR</div><div className="font-mono text-amber-400">{target.ctr?.toFixed(2)}%</div></div>
                <div><div className="text-zinc-500">Impressions</div><div className="font-mono text-zinc-200">{(target.impressions || 0).toLocaleString()}</div></div>
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5 block">Daily budget (USD)</Label>
              <Input type="number" min={1} value={budget} onChange={(e) => setBudget(e.target.value)} className="bg-zinc-950 border-zinc-800 rounded-sm h-10" data-testid="run-ads-budget" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5 block">Landing URL (optional)</Label>
              <Input value={landingUrl} onChange={(e) => setLandingUrl(e.target.value)} placeholder="https://yoursite.com/page-targeting-this-query" className="bg-zinc-950 border-zinc-800 rounded-sm h-10" data-testid="run-ads-landing" />
              <p className="text-[10px] text-zinc-600 mt-1.5">Leave blank — AI will pick a sensible default.</p>
            </div>
          </div>
        ) : (
          <div className="bg-zinc-950 border border-[#10B981]/40 rounded-sm p-4 space-y-3" data-testid="run-ads-success">
            <div className="flex items-center gap-2 text-[#10B981]">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Campaign built</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div><div className="text-zinc-500">Ads</div><div className="font-display text-lg text-zinc-100">{created.ads?.length || 0}</div></div>
              <div><div className="text-zinc-500">Keyword groups</div><div className="font-display text-lg text-zinc-100">{created.keyword_groups?.length || 0}</div></div>
              <div><div className="text-zinc-500">Est. clicks/day</div><div className="font-display text-lg text-zinc-100">{created.estimated_clicks_per_day || 0}</div></div>
            </div>
            <div className="text-xs text-zinc-500">Recommended budget: <span className="text-zinc-300">${created.recommended_budget_usd}/day</span></div>
          </div>
        )}

        <DialogFooter>
          {!created ? (
            <>
              <Button variant="outline" onClick={onClose} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="run-ads-cancel">Cancel</Button>
              <Button onClick={create} disabled={creating} className="bg-[#10B981] hover:bg-[#10B981]/90 text-[#062D24] rounded-sm" data-testid="run-ads-build">
                {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</> : <><Megaphone className="w-4 h-4 mr-2" /> Build campaign</>}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="run-ads-done">
                <X className="w-4 h-4 mr-2" /> Close
              </Button>
              <Button onClick={() => { onClose(); navigate("/google-ads"); }} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="run-ads-open-campaign">
                Open campaign <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
